// src/config/certificateTemplates.js

export const CERTIFICATE_TYPES = [
  {
    id: 'internship',
    label: 'Internship',
    icon: '🎓',
    tone: '#1e40af',
    defaultTitle: 'Certificate of Internship',
    description: 'Internship completion',
    requiresStartDate: true,
    requiresEndDate: true,
  },
  {
    id: 'workshop',
    label: 'Workshop',
    icon: '🛠️',
    tone: '#0e7490',
    defaultTitle: 'Certificate of Workshop',
    description: 'Hands-on workshop participation',
    requiresStartDate: true,
    requiresEndDate: true,
  },
  {
    id: 'course',
    label: 'Course',
    icon: '📘',
    tone: '#7c3aed',
    defaultTitle: 'Certificate of Course Completion',
    description: 'Online / offline course completion',
    requiresStartDate: true,
    requiresEndDate: true,
  },
  {
    id: 'training',
    label: 'Training',
    icon: '🧑‍🏫',
    tone: '#047857',
    defaultTitle: 'Certificate of Training',
    description: 'Industrial / corporate training',
    requiresStartDate: true,
    requiresEndDate: true,
  },
  {
    id: 'completion',
    label: 'Completion',
    icon: '✅',
    tone: '#b45309',
    defaultTitle: 'Certificate of Completion',
    description: 'Generic program completion',
    requiresStartDate: true,
    requiresEndDate: true,
  },
  {
    id: 'appreciation',
    label: 'Appreciation',
    icon: '🏅',
    tone: '#be123c',
    defaultTitle: 'Certificate of Appreciation',
    description: 'Recognition / contribution',
    requiresStartDate: false,
    requiresEndDate: false,
  },
];

export const PARTICIPANT_TYPES = [
  {
    id: 'student',
    label: 'Student',
    icon: '🎓',
    hint: 'Currently pursuing studies',
  },
  {
    id: 'professional',
    label: 'Non-Student / Professional',
    icon: '💼',
    hint: 'Working professional, freelancer or other',
  },
];

export const getCertificateType = (id) =>
  CERTIFICATE_TYPES.find((c) => c.id === id) || CERTIFICATE_TYPES[0];

export const getParticipantType = (id) =>
  PARTICIPANT_TYPES.find((p) => p.id === id) || PARTICIPANT_TYPES[0];

/* ==========================================================================
   ADAPTIVE DECLARATION RENDERERS
   ========================================================================== */

const H = ({ children }) => (
  <span className="cert-highlight">{children}</span>
);

export function renderDeclaration({ data, orgName, fmtShort }) {
  const type = data.certificateType || 'internship';
  const isProfessional = data.participantType === 'professional';

  const field = data.fieldName || 'Course / Technology / Department';
  const from = fmtShort(data.startDate);
  const to = fmtShort(data.endDate);
  const range = (
    <>
      <H>{from}</H> to <H>{to}</H>
    </>
  );

  const org = orgName || data.organizationName || '';

  /* -------------------- INTERNSHIP (matches image) -------------------- */
  if (type === 'internship') {
    return (
      <>
        has successfully completed the College Internship Program at{' '}
        <strong>AVTECH Baramati Pvt. Ltd.</strong> in the field of
        <br />
        <span className="cert-course-line"> {field} </span>
      </>
    );
  }

  /* -------------------- WORKSHOP -------------------- */
  if (type === 'workshop') {
    if (isProfessional) {
      return org ? (
        <>
          associated with <H>{org}</H>, has actively participated in the{' '}
          <H>{data.title || 'Workshop'}</H> workshop conducted from {range} at{' '}
          <strong>AV Tech Baramati Private Limited.</strong>, gaining
          practical exposure to <H>{field}</H>.
        </>
      ) : (
        <>
          has actively participated in the <H>{data.title || 'Workshop'}</H>{' '}
          workshop conducted from {range} at{' '}
          <strong>AV Tech Baramati Private Limited.</strong>, gaining
          practical exposure to <H>{field}</H>.
        </>
      );
    }
    return (
      <>
        has actively participated in the <H>{data.title || 'Workshop'}</H>{' '}
        workshop conducted from {range} at{' '}
        <strong>AV Tech Baramati Private Limited.</strong>, gaining hands-on
        exposure to <H>{field}</H>.
      </>
    );
  }

  /* -------------------- COURSE -------------------- */
  if (type === 'course') {
    if (isProfessional) {
      return org ? (
        <>
          associated with <H>{org}</H>, has successfully completed the{' '}
          <H>{data.title || 'Course'}</H> course in the domain of{' '}
          <H>{field}</H> from {range}, delivered by{' '}
          <strong>AV Tech Baramati Private Limited.</strong>.
        </>
      ) : (
        <>
          has successfully completed the <H>{data.title || 'Course'}</H>{' '}
          course in the domain of <H>{field}</H> from {range}, delivered by{' '}
          <strong>AV Tech Baramati Private Limited.</strong>.
        </>
      );
    }
    return (
      <>
        has successfully completed the <H>{data.title || 'Course'}</H> course
        in the domain of <H>{field}</H> from {range}, delivered by{' '}
        <strong>AV Tech Baramati Private Limited.</strong>.
      </>
    );
  }

  /* -------------------- TRAINING -------------------- */
  if (type === 'training') {
    if (isProfessional) {
      return org ? (
        <>
          associated with <H>{org}</H>, has successfully completed an
          industrial training program in the field of <H>{field}</H> from{' '}
          {range}, conducted at{' '}
          <strong>AV Tech Baramati Private Limited.</strong>.
        </>
      ) : (
        <>
          has successfully completed an industrial training program in the
          field of <H>{field}</H> from {range}, conducted at{' '}
          <strong>AV Tech Baramati Private Limited.</strong>.
        </>
      );
    }
    return (
      <>
        has successfully completed an industrial training program in the field
        of <H>{field}</H> from {range}, conducted at{' '}
        <strong>AV Tech Baramati Private Limited.</strong>.
      </>
    );
  }

  /* -------------------- COMPLETION -------------------- */
  if (type === 'completion') {
    if (isProfessional) {
      return org ? (
        <>
          associated with <H>{org}</H>, has successfully completed the{' '}
          <H>{data.title || 'Program'}</H> program in the field of{' '}
          <H>{field}</H> from {range}, conducted at{' '}
          <strong>AV Tech Baramati Private Limited.</strong>.
        </>
      ) : (
        <>
          has successfully completed the <H>{data.title || 'Program'}</H>{' '}
          program in the field of <H>{field}</H> from {range}, conducted at{' '}
          <strong>AV Tech Baramati Private Limited.</strong>.
        </>
      );
    }
    return (
      <>
        has successfully completed the <H>{data.title || 'Program'}</H>{' '}
        program in the field of <H>{field}</H> from {range}, conducted at{' '}
        <strong>AV Tech Baramati Private Limited.</strong>.
      </>
    );
  }

  /* -------------------- APPRECIATION -------------------- */
  if (type === 'appreciation') {
    if (isProfessional) {
      return org ? (
        <>
          associated with <H>{org}</H>, is hereby sincerely appreciated for
          outstanding contribution and dedication towards{' '}
          <H>{data.title || data.fieldName || 'the initiative'}</H>. Their
          professionalism and commitment have been truly commendable.
        </>
      ) : (
        <>
          is hereby sincerely appreciated for outstanding contribution and
          dedication towards{' '}
          <H>{data.title || data.fieldName || 'the initiative'}</H>. Their
          professionalism and commitment have been truly commendable.
        </>
      );
    }
    return (
      <>
        is hereby sincerely appreciated for outstanding contribution,
        discipline and dedication towards{' '}
        <H>{data.title || data.fieldName || 'the initiative'}</H>. Their
        enthusiasm and commitment have been truly commendable.
      </>
    );
  }

  /* -------------------- FALLBACK -------------------- */
  return (
    <>
      has successfully completed the program in the field of <H>{field}</H>{' '}
      from {range}, conducted at{' '}
      <strong>AV Tech Baramati Private Limited.</strong>.
    </>
  );
}

/* ==========================================================================
   ADAPTIVE SKILLS / CLOSING SENTENCE
   ========================================================================== */

export function renderSkillsLine({ data }) {
  const type = data.certificateType || 'internship';
  const skills = data.skills || 'practical skills & industry tools';

  if (type === 'appreciation') {
    return (
      <>
        We wish them continued success in all their future endeavours and look
        forward to their valuable contributions to the industry.
      </>
    );
  }

  if (type === 'workshop') {
    return (
      <>
        The participant demonstrated keen interest and was actively exposed to{' '}
        <span className="cert-highlight">{skills}</span> throughout the
        workshop.
      </>
    );
  }

  if (type === 'course') {
    return (
      <>
        The candidate demonstrated strong commitment and successfully mastered{' '}
        <span className="cert-highlight">{skills}</span> covered during the
        course.
      </>
    );
  }

  if (type === 'training') {
    return (
      <>
        Throughout the training, the candidate demonstrated dedication and was
        actively exposed to{' '}
        <span className="cert-highlight">{skills}</span> in a professional
        environment.
      </>
    );
  }

  if (type === 'completion') {
    return (
      <>
        The candidate demonstrated commitment throughout the program and was
        actively exposed to <span className="cert-highlight">{skills}</span>.
      </>
    );
  }

  // internship (default) — matches the image exactly
  return (
    <>
      During the internship period, the student demonstrated dedication, active
      participation, and a strong willingness to learn. We wish them all the
      best for their future endeavors.
    </>
  );
}